<template>
  <div class="referral-headline">
    <!-- PC は1行に流し、SP だけ JSON の行区切りで折り返す（.._badge-line が block になる） -->
    <p class="referral-headline__badge">
      <span
        class="referral-headline__badge-line"
        v-for="line in badgeLines"
        :key="line"
        >{{ line }}</span
      >
    </p>

    <div class="referral-headline__title-row">
      <span class="referral-headline__deco referral-headline__deco--left">
        <i
          class="referral-headline__deco-bar referral-headline__deco-bar--blue"
        ></i>
        <i
          class="referral-headline__deco-bar referral-headline__deco-bar--green"
        ></i>
      </span>
      <span class="referral-headline__titles">
        <span class="referral-headline__title">{{ copy.title }}</span>
        <span class="referral-headline__period">{{ copy.period }}</span>
      </span>
      <span class="referral-headline__deco referral-headline__deco--right">
        <i
          class="referral-headline__deco-bar referral-headline__deco-bar--green"
        ></i>
        <i
          class="referral-headline__deco-bar referral-headline__deco-bar--blue"
        ></i>
      </span>
    </div>

    <div class="referral-headline__benefit" v-if="showBenefit">
      <div class="referral-headline__roles">
        <span class="referral-headline__role">{{ copy.inviterLabel }}</span>
        <span class="referral-headline__role">{{ copy.inviteeLabel }}</span>
      </div>
      <div class="referral-headline__box">
        <div class="referral-headline__col">
          <span class="referral-headline__plan">{{ copy.benefitPlan }}</span>
          <span class="referral-headline__value">{{ copy.benefitValue }}</span>
          <span class="referral-headline__multiplier">{{
            copy.inviterMultiplier
          }}</span>
        </div>
        <div class="referral-headline__col">
          <span class="referral-headline__plan">{{ copy.benefitPlan }}</span>
          <span class="referral-headline__value">{{ copy.benefitValue }}</span>
        </div>
        <!-- イラストは装飾なので alt は空（読み上げは前後のテキストで足りる）。
             仕切り線の上に重ねるため、ボックスの最後に置く -->
        <img
          class="referral-headline__owl"
          src="/static/img/yagiprime-referral/owl-letter.png"
          alt=""
          width="400"
          height="299"
        />
      </div>
    </div>

    <!-- 対比図の位置に別の中身を差す（マイページは紹介の状況をここに置く） -->
    <div class="referral-headline__slot" v-else>
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 紹介キャンペーンの見出し（バッジ・タイトル・期間・特典の対比図）。
 *
 * シェア枠（ReferralShareOfferBox）から使う表示専用コンポーネント。共有ボタンや
 * 認証まわりの状態を持たないので、見出しだけを別ページ（マイページ・LP など）へ
 * 出したくなったときもそのまま置ける。
 *
 * 文言はすべて JSON カタログ（referralCampaign.json の headline）が正本。**期間の
 * 表記はリリース時に必ず更新する**（JSON 側のコメント参照）。
 */
import { computed } from 'vue'
import campaign from '@/utils/yagiprime/referralCampaign.json'

const props = defineProps({
  /**
   * 「紹介した方 / 紹介された方」の対比図を出すか。
   * false のときは同じ位置にスロットの中身を出す（マイページは紹介の状況）。
   */
  showBenefit: {
    type: Boolean,
    required: false,
    default: true
  },
  /**
   * 吹き出しの文言を画面ごとに言い換えるときだけ渡す（1要素 = 1行）。
   * 既定は JSON の headline.badgeLines（紹介する側への訴求）。被紹介者の着地は
   * 「紹介すると」ではなく「紹介でアクセスした」立場なので差し替える。
   */
  badgeLines: {
    type: Array,
    required: false,
    default: null
  }
})

const copy = campaign.headline

const badgeLines = computed(() => props.badgeLines || copy.badgeLines)
</script>

<style scoped>
.referral-headline {
  text-align: center;
}

/* 吹き出し。下辺中央のしっぽはタイトルを指す三角形 */
.referral-headline__badge {
  position: relative;
  display: inline-block;
  margin: 0;
  padding: 7px 18px;
  border-radius: 999px;
  background: #0f9934;
  color: #fff;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.4;
}

.referral-headline__badge::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  width: 0;
  height: 0;
  transform: translateX(-50%);
  border-top: 9px solid #0f9934;
  border-right: 8px solid transparent;
  border-left: 8px solid transparent;
}

.referral-headline__badge-line {
  display: inline;
}

.referral-headline__title-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 12px;
}

.referral-headline__titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.referral-headline__title {
  font-size: 34px;
  font-weight: bold;
  line-height: 1.25;
  letter-spacing: 0.06em;
  color: #404040;
}

.referral-headline__period {
  font-size: 26px;
  font-weight: bold;
  line-height: 1.3;
  color: #404040;
}

/* 装飾の斜線。志望動機AIパネルの枠グラデーションと同じ青・緑を使う */
.referral-headline__deco {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  align-self: flex-start;
  margin-top: 6px;
}

.referral-headline__deco-bar {
  display: block;
  width: 4px;
  height: 52px;
  border-radius: 999px;
}

.referral-headline__deco-bar--green {
  background: hsl(124deg 51% 52%);
}

/* 青は常に外側・下段で、緑より短く寝かせる（デザイン指定） */
.referral-headline__deco-bar--blue {
  height: 38px;
  margin-top: 20px;
  background: hsl(198deg 79% 54%);
}

/* 斜線の向きはタイトルを挟んで外向き */
.referral-headline__deco--left .referral-headline__deco-bar {
  transform: rotate(-18deg);
}

.referral-headline__deco--left .referral-headline__deco-bar--blue {
  transform: rotate(-36deg);
}

.referral-headline__deco--right .referral-headline__deco-bar {
  transform: rotate(18deg);
}

.referral-headline__deco--right .referral-headline__deco-bar--blue {
  transform: rotate(36deg);
}

/* 対比図の代わりに差し込む中身。対比図と同じ位置・同じ幅に収める */
.referral-headline__slot {
  max-width: 520px;
  margin: 22px auto 0;
}

/* 対比図。ヤギはボックスの上辺にまたがるので、包む側を位置の基準にする */
.referral-headline__benefit {
  max-width: 520px;
  margin: 22px auto 0;
  padding-top: 12px;
}

.referral-headline__roles {
  display: flex;
}

.referral-headline__role {
  flex: 1 1 0;
  font-size: 16px;
  font-weight: bold;
  color: #004d3f;
  line-height: 1.2;
}

/* ボックスの高さを基準に置くので、幅が変わっても重なり方は変わらない */
.referral-headline__owl {
  position: absolute;
  top: -36%;
  left: 50%;
  width: 25%;
  height: auto;
  transform: translateX(-50%) scaleX(-1);
  pointer-events: none;
}

.referral-headline__box {
  position: relative;
  display: flex;
  margin-top: 8px;
  border: 2px solid #004d3f;
  border-radius: 10px;
  background: #fff;
}

.referral-headline__col {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-width: 0;
  padding: 14px 12px 10px;
}

.referral-headline__col + .referral-headline__col {
  border-left: 2px solid #004d3f;
}

.referral-headline__plan {
  font-size: 15px;
  font-weight: bold;
  line-height: 1.3;
  color: #004d3f;
}

.referral-headline__value {
  font-size: 28px;
  font-weight: bold;
  line-height: 1.25;
  color: #004d3f;
}

/* 「×紹介人数」は紹介した方の欄だけ。右下に寄せ、上限の注記（下部）へ ※ でつなぐ */
.referral-headline__multiplier {
  align-self: flex-end;
  font-size: 14px;
  font-weight: bold;
  line-height: 1.2;
  color: #004d3f;
}

@media screen and (width <= 480px) {
  .referral-headline__badge {
    padding: 6px 12px;
    font-size: 12px;
  }

  .referral-headline__badge-line {
    display: block;
  }

  .referral-headline__title-row {
    gap: 6px;
  }

  .referral-headline__title {
    font-size: 24px;
    letter-spacing: 0.02em;
  }

  .referral-headline__period {
    font-size: 19px;
  }

  .referral-headline__deco-bar {
    height: 32px;
    width: 3px;
  }

  .referral-headline__deco-bar--blue {
    height: 24px;
    margin-top: 14px;
  }

  .referral-headline__role {
    font-size: 13px;
  }

  .referral-headline__col {
    padding: 10px 8px 8px;
  }

  .referral-headline__plan {
    font-size: 12px;
  }

  .referral-headline__value {
    font-size: 21px;
  }

  .referral-headline__multiplier {
    font-size: 11px;
  }
}
</style>
